// AbilityDisplays.tsx - Container for multiple AbilityDisplay components
import React, { useContext } from 'react';
import AbilityDisplay from './AbilityDisplay';
import { SimplifiedSpell } from '../../../../../gameTypes';
import GameAssetsContext from '../../../../../context/GameAssetContext';

interface AbilityDisplaysProps {
  spellImageUrls: Record<string, string>;
  autoAttackImageUrl: string;
  passiveImageUrl: string;
  abilities: SimplifiedSpell[];
  attackRange?: number;
  compactMode?: boolean;
  onAbilityClick?: (abilityKey: string) => void;
}

const AbilityDisplays: React.FC<AbilityDisplaysProps> = ({
  spellImageUrls,
  autoAttackImageUrl,
  passiveImageUrl,
  abilities,
  attackRange,
  compactMode = false,
  onAbilityClick,
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const abilityKeys = ['Q', 'W', 'E', 'R'];

  // Add debugging and safety checks
  //console.log('AbilityDisplays - abilities array:', abilities);
  //console.log('AbilityDisplays - abilities length:', abilities?.length);

  // Create mock spells for passive and auto attack (they might not have cooldown data)
  const createMockSpell = (name: string) => ({
    name: name,
    description: '',
    cooldown: 0,
    remainingCooldown: 0,
    cooldownDurations: [],
    isOnCooldown: false,
    damage: [],
    manaCost: [],
    scalings: [],
    pointsInSpell: 0,
    range: 0,
    imageState: '',
    additionalInfo: {},
  });

  // Safety check for abilities array
  if (!abilities || abilities.length === 0) {
    console.warn('AbilityDisplays - No abilities data available');
    return <div className="ability-displays-container">No abilities data</div>;
  }

  return (
    <div className="ability-displays-container">
      {/* Passive */}
      <AbilityDisplay
        key="P"
        abilityKey="P"
        abilityImage={passiveImageUrl}
        spell={createMockSpell('Passive')}
        isAutoAttack={true} // Treat like auto attack (no cooldown/mana info)
        compactMode={compactMode}
        onAbilityClick={onAbilityClick}
      />

      {/* Regular Abilities */}
      {abilityKeys.map((key, index) => {
        const spell = abilities[index];
        if (!spell) {
          console.warn(`AbilityDisplays - Missing spell at index ${index} for key ${key}`);
          return (
            <AbilityDisplay
              key={key}
              abilityKey={key}
              abilityImage={spellImageUrls[key] || ''}
              spell={createMockSpell(`Ability ${key}`)}
              compactMode={compactMode}
              onAbilityClick={onAbilityClick}
            />
          );
        }
        
        return (
          <AbilityDisplay
            key={key}
            abilityKey={key}
            abilityImage={spellImageUrls[key] || ''}
            spell={spell}
            compactMode={compactMode}
            onAbilityClick={onAbilityClick}
          />
        );
      })}
      
      {/* Auto Attack */}
      <div className="auto-attack-with-range">
        <AbilityDisplay
          key="A"
          abilityKey="A"
          abilityImage={autoAttackImageUrl}
          spell={abilities[4] || abilities[5] || createMockSpell('Auto Attack')}
          isAutoAttack={true}
          compactMode={compactMode}
          onAbilityClick={onAbilityClick}
        />
        {attackRange !== undefined && !compactMode && (
          <div className="attack-range-display">
            <img 
              src={gameWideAssets.attackRangeIcon} 
              alt="Range" 
              className="ability-info-icon"
            />
            {Math.round(attackRange)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AbilityDisplays; 